import type { FulfillmentMode, UserType } from '@/pages/POS/types';
import type { ShippingMode } from './shippingTotals';
import {
  effectiveShippingMode,
  type FulfillmentChoice,
} from '../components/Fulfillment/fulfillmentChoice';

export interface CustomerCheckoutValues {
  user_type?: UserType;
  customer_phone?: string;
  customer_name?: string;
  citizen_id?: string;
  customer_city_id?: string;
  customer_ward_id?: string;
  customer_address?: string;
  care_staff_id?: string;
  delivery_days?: number;
  receive_method?: 'delivery' | 'pickup' | 'vault';
  pickup_store_id?: string;
  delivery_name?: string;
  delivery_phone?: string;
  invoice_name?: string;
  invoice_tax_id?: string;
  invoice_address?: string;
  buyer_name?: string;
  buyer_cccd?: string;
  shipping_mode?: ShippingMode;
  shipping_partner?: string;
  shipping_pickup_location_id?: string;
  package_weight?: number;
}

/** Một trường bắt buộc còn trống: tên field trong antd Form + nhãn hiển thị. */
export interface MissingCustomerField {
  name: string;
  label: string;
}

/** Explicit caller-owned validation context; legacy callers remain strict. */
export type CustomerValidationPolicy =
  | { scope: 'strict' }
  | { scope: 'pos'; status: 'loading' | 'error' }
  | { scope: 'pos'; status: 'ready'; personalCccdRequired: boolean };

export const STRICT_CUSTOMER_VALIDATION_POLICY: CustomerValidationPolicy = {
  scope: 'strict',
};

export interface CustomerValidationRequirements {
  isResolved: boolean;
  isError: boolean;
  personalCccdRequired: boolean;
  personalInvoiceOptional: boolean;
}

export function customerValidationRequirements(
  policy: CustomerValidationPolicy = STRICT_CUSTOMER_VALIDATION_POLICY,
): CustomerValidationRequirements {
  if (policy.scope === 'strict') {
    return {
      isResolved: true,
      isError: false,
      personalCccdRequired: true,
      personalInvoiceOptional: false,
    };
  }
  if (policy.status === 'ready') {
    return {
      isResolved: true,
      isError: false,
      personalCccdRequired: policy.personalCccdRequired,
      personalInvoiceOptional: true,
    };
  }
  return {
    isResolved: false,
    isError: policy.status === 'error',
    personalCccdRequired: true,
    personalInvoiceOptional: false,
  };
}

export function customerValidationPolicyProblem(
  policy: CustomerValidationPolicy,
): string | undefined {
  if (policy.scope !== 'pos' || policy.status === 'ready') return undefined;
  return policy.status === 'error'
    ? 'Không thể tải cấu hình xác thực khách hàng POS'
    : 'Đang tải cấu hình xác thực khách hàng POS';
}

/**
 * Các trường khách hàng còn trống khiến không ghi được hoá đơn, theo thứ tự
 * xuất hiện trong dialog. Mảng rỗng = đủ điều kiện.
 *
 * Đây là NGUỒN SỰ THẬT DUY NHẤT của cổng checkout — `isCustomerReady` suy ra từ
 * hàm này. `name` dùng để đối chiếu với `errorFields` của `form.validateFields()`
 * (tránh liệt kê trùng một trường hai lần trong toast) và để `scrollToField`.
 *
 * Lưu ý: hàm này chỉ phát hiện trường TRỐNG. Lỗi ĐỊNH DẠNG (CCCD không đủ 12 số,
 * SĐT sai...) do rules của antd bắt — xem `buildCheckoutProblems`.
 */
export function missingCustomerFields(
  v: CustomerCheckoutValues,
  fulfillmentMode: FulfillmentMode = 'INSTANT',
  policy: CustomerValidationPolicy = STRICT_CUSTOMER_VALIDATION_POLICY,
): MissingCustomerField[] {
  const missing: MissingCustomerField[] = [];
  const add = (name: string, label: string) => missing.push({ name, label });
  const requirements = customerValidationRequirements(policy);
  const isPersonal = (v.user_type ?? 'personal') === 'personal';
  const posRequiresPersonalCccd =
    policy.scope === 'pos' &&
    policy.status === 'ready' &&
    policy.personalCccdRequired;

  if (!v.customer_phone) add('customer_phone', 'Số điện thoại khách');
  if (isPersonal && posRequiresPersonalCccd && !v.citizen_id) {
    add('citizen_id', 'CCCD khách hàng');
  }
  if (!v.customer_city_id) add('customer_city_id', 'Tỉnh/Thành phố');
  if (!v.customer_ward_id) add('customer_ward_id', 'Phường/Xã');
  if (!v.customer_address) add('customer_address', 'Địa chỉ chi tiết');
  // [BỎ N.BÁN + NV CHĂM SÓC] Nhân viên chăm sóc không còn là trường bắt buộc —
  // dropdown đã gỡ khỏi panel POS, BE nhận optional. Khôi phục: bỏ comment dòng dưới.
  // if (!v.care_staff_id) add('care_staff_id', 'Nhân viên chăm sóc');

  const method = v.receive_method ?? 'delivery';
  if (method === 'delivery') {
    if (!v.delivery_name) add('delivery_name', 'Họ tên người nhận');
    if (!v.delivery_phone) add('delivery_phone', 'SĐT người nhận');

    // Task 7b: KHÔNG còn phép kiểm `shipping_mode` ở đây. Luật đã chuyển sang biên
    // đọc (`effectiveShippingMode`, `fulfillmentChoice.ts`) — đơn giao hàng thiếu
    // field này được ĐỌC LÀ `'self'`, không phải bị chặn. Giữ phép kiểm ở đây
    // (ghi vào Form để hết trạng thái rỗng) tạo dead-end cho `Orders/edit`: trang
    // đó không render thanh phương thức nhận hàng nên không có ô nào để gõ, và
    // mọi đơn giao hàng tạo trước tính năng vận chuyển đều có field này rỗng ⇒
    // vĩnh viễn không sửa được.
    //
    // Đơn vị vận chuyển + khối lượng gói KHÔNG kiểm ở đây — xem
    // `missingCarrierFields`.
  }
  // `pickup_store_id` (pickup/vault) CỐ TÌNH không kiểm ở đây — đây là fix cho
  // đúng lớp bug mà Task 7b đã chữa cho `shipping_mode`, bỏ sót trường anh em.
  // Ô này đã chuyển hẳn ra `BranchSelectField` trong `FulfillmentBar` (thanh
  // PTNH neo đáy cột hàng) và tự mang `pickupStoreRules` (required, xem
  // `customerFormSchema.ts`). Trên POS ô LUÔN mount khi `receive_method !==
  // 'delivery'`, nên `form.validateFields()` (gọi KHÔNG tham số ở
  // `handleCheckout`) tự bắt và tô đỏ đúng ô — khai trùng ở đây chỉ để phòng
  // hờ, không thêm giá trị.
  //
  // Lý do BẮT BUỘC phải bỏ, không chỉ "được phép" bỏ: `Orders/edit.tsx` dùng
  // `CustomerSidebar variant="compact"` và KHÔNG render `FulfillmentBar` —
  // `BranchSelectField` không tồn tại trên trang đó nên `pickupStoreRules`
  // không bao giờ chạy. Nếu vẫn `add()` field này ở đây, mọi đơn pickup/vault
  // TẠO TRƯỚC khi có `FulfillmentBar` (thiếu `pickup_store_id` do BE cũ trước
  // fix `pos-order.service.ts:754` chưa ghi `store_id`) sẽ vĩnh viễn không lưu
  // được trên `Orders/edit`: `requiresCustomerDialog(['pickup_store_id'])` =
  // false (đã đổi ở Task 7 vì ô rời khỏi dialog) nên dialog không mở, mà toast
  // vẫn trỏ vào field không tồn tại trên màn hình — thu ngân bấm Lưu, thấy
  // toast, không có ô nào để sửa.
  //
  // KHÔNG chuẩn hoá giá trị (khác `shipping_mode`) vì không cần: đơn cũ thiếu
  // trường này vẫn hợp lệ để XEM/SỬA các trường khác trên `Orders/edit`, chỉ
  // không sửa được CHÍNH `pickup_store_id` ở đó — chấp nhận được vì trang đó
  // không có ô để sửa nó dù có chặn hay không.

  // Nhãn ghép tiền tố "Hoá đơn:" vì 4 ô này nằm trong tab "THÔNG TIN XUẤT HOÁ
  // ĐƠN" của dialog "Thông tin khách hàng" (mở qua icon sửa trên thẻ khách,
  // xem `CustomerSidebar/index.tsx` — nút chữ "Sửa thông tin KH" đã bỏ hẳn) —
  // chỗ user hay bỏ sót nhất với khách cá nhân.
  if (!isPersonal || !requirements.personalInvoiceOptional) {
    if (!v.invoice_name) add('invoice_name', 'Hoá đơn: Tên khách hàng / công ty');
    if (!v.invoice_tax_id) add('invoice_tax_id', 'Hoá đơn: CCCD/MST');
    if (!v.invoice_address) add('invoice_address', 'Hoá đơn: Địa chỉ xuất hoá đơn');
  }
  // TCR-2618 — "CCCD người mua" (`buyer_cccd`) KHÔNG còn là trường bắt buộc:
  // để trống vẫn ghi được đơn (POS lẫn sửa đơn), kể cả khách tổ chức và kể cả
  // khi setting `pos_personal_cccd_required` đang bật. Ô này chỉ còn luật
  // ĐỊNH DẠNG do rules của antd bắt (xem `getBuyerCccdRules`).
  //
  // Lưu ý: CCCD khách hàng (`citizen_id`) vẫn bắt buộc theo setting
  // `pos_personal_cccd_required` — chỉ `buyer_cccd` được bỏ.

  // Đơn đặt trước bắt buộc có hạn giao — khớp với @ValidateIf(PREORDER) ở BE DTO.
  const hasDeliveryDeadline =
    fulfillmentMode !== 'PREORDER' ||
    (typeof v.delivery_days === 'number' && v.delivery_days > 0);
  if (!hasDeliveryDeadline) add('delivery_days', 'Hạn giao hàng');

  return missing;
}

/**
 * Hai ô của `CarrierPanel` (cột ĐVVC) + khối lượng gói ở đáy cột hàng.
 *
 * Vẫn tách khỏi `missingCustomerFields` dù cả hai giờ cùng gác một cửa (nút "Ghi
 * hoá đơn và thanh toán"): hai hàm mô tả hai nhóm điều kiện khác nhau —
 * `missingCustomerFields` là thông tin khách/hoá đơn, hàm này là thông tin gửi
 * cho hãng vận chuyển. `useMissingCustomerFields` (hook watch live, dùng để quyết
 * định có mở CustomerDialog hay không) chỉ đọc hàm trên; hai ô dưới đây không
 * nằm trong dialog nên không được kéo vào đó.
 *
 * Lịch sử: khi còn Drawer thanh toán, hai ô này CHỈ nhập được bên trong Drawer
 * nên cổng của chúng buộc phải ở nút xác nhận trong Drawer — đặt ở cổng MỞ Drawer
 * sẽ thành thế bí (không mở được Drawer để chọn hãng, mà không chọn hãng thì
 * không mở được Drawer). Drawer đã gỡ, `CarrierPanel` hiện thẳng ở cột giữa.
 *
 * Chỉ áp cho đơn giao hàng qua ĐVVC — xét theo `shipping_mode` HIỆU LỰC
 * (`effectiveShippingMode`), KHÔNG phải giá trị thô của `v.shipping_mode`.
 *
 * Final review fix (C1, critical): trước fix này hàm đọc thẳng `v.shipping_mode`
 * thô. Tab mới mở ở chế độ giao hàng mà thu ngân CHƯA bấm thanh PTNH có
 * `shipping_mode` thô rỗng (Task 7b cố tình xoá effect từng ghi `'self'` lúc
 * mount) — trước Task 4, biên đọc luôn coi rỗng là `'self'` nên `!== 'carrier'`
 * đúng ý. Từ khi `pos_default_fulfillment_choice` mặc định là `'carrier'`, đơn
 * đó HIỆU LỰC là `'carrier'` (xem `buildCreateOrderPayload`) nhưng hàm này vẫn
 * đọc thô ra `undefined !== 'carrier'` ⇒ trả `[]` ⇒ cổng không chặn dù đơn sắp
 * ghi ra thiếu sạch hãng/nơi lấy hàng/khối lượng. `effectiveShippingMode` đã tự
 * gộp luôn việc `receive_method` khác `'delivery'` trả `undefined` (không bao
 * giờ thành `'carrier'`), nên không cần khai riêng `method` như trước — vẫn
 * đúng tinh thần "không phụ thuộc việc dọn `shipping_mode` khi đổi phương thức
 * nhận hàng luôn chạy đúng", vì `effectiveShippingMode` tính lại từ
 * `receive_method` mỗi lần, không tin giá trị `shipping_mode` còn sót lại.
 */
export function missingCarrierFields(
  v: CustomerCheckoutValues,
  defaultFulfillmentChoice: FulfillmentChoice,
): MissingCustomerField[] {
  const effectiveMode = effectiveShippingMode(
    v.receive_method,
    v.shipping_mode,
    defaultFulfillmentChoice,
  );
  if (effectiveMode !== 'carrier') return [];

  const missing: MissingCustomerField[] = [];
  if (!v.shipping_partner) missing.push({ name: 'shipping_partner', label: 'Đơn vị vận chuyển' });
  // Thiếu nơi lấy hàng thì hãng không có địa chỉ để tới gom kiện, và phí trên
  // đơn cũng chưa từng được tính (`buildEstimatePayload` trả `null`) — tức đơn
  // ghi ra sẽ mang phí 0 rồi lệch tiền ngay khi tạo vận đơn.
  if (!v.shipping_pickup_location_id) {
    missing.push({ name: 'shipping_pickup_location_id', label: 'Nơi lấy hàng' });
  }
  if (!(typeof v.package_weight === 'number' && v.package_weight > 0)) {
    missing.push({ name: 'package_weight', label: 'Khối lượng gói hàng phải lớn hơn 0' });
  }
  return missing;
}

export function isCustomerReady(
  v: CustomerCheckoutValues,
  fulfillmentMode: FulfillmentMode = 'INSTANT',
  policy: CustomerValidationPolicy = STRICT_CUSTOMER_VALIDATION_POLICY,
): boolean {
  return missingCustomerFields(v, fulfillmentMode, policy).length === 0;
}

export function deriveDeliverySnapshot(
  v: CustomerCheckoutValues,
): { customer_name: string; citizen_id: string } {
  const citizenId =
    v.user_type === 'organization' ? v.buyer_cccd ?? '' : v.citizen_id ?? '';
  return {
    customer_name: v.customer_name || v.buyer_name || v.invoice_name || '',
    citizen_id: citizenId,
  };
}
